// Pure commission math (no Firebase) so it can be unit tested. Numbers come from config/commissionPlans.
import { COMMISSION } from '../config/commissionPlans';

export const isClosedDeal = (t) => String(t.status || '').toLowerCase() === 'closed' || !!t.actualClosingDate;
export const isOpenDeal = (t) => !isClosedDeal(t) && String(t.status || '').toLowerCase() !== 'lost';
export const priceOf = (t) => parseFloat(String(t.purchasePrice || t.offerPrice || t.listingPrice || t.price || 0).replace(/[^0-9.]/g, '')) || 0;
export const grossOf = (t) => {
  const type = String(t.commissionType || '').toLowerCase();
  if (type === 'percentage') return priceOf(t) * (parseFloat(t.commissionPercentage) || 0) / 100;
  if (type === 'dollar') return parseFloat(t.commissionDollarAmount) || 0;
  return 0;
};
export const closeDateOf = (t) => new Date(t.actualClosingDate || t.closingDate || t.anticipatedClosingDate || 0);

// Lead source on the deal: 'agent-generated' / 'agent' -> 'agent'; 'team-generated' / 'team' -> 'team'; else null (not set).
export const leadSourceOf = (t) => {
  const v = String(t?.leadSource || '').toLowerCase();
  if (v.startsWith('agent')) return 'agent';
  if (v.startsWith('team')) return 'team';
  return null;
};

// Agent's own share (%) of one deal from their commission settings, by the deal's lead source:
// agentLeadPercent for agent-generated (e.g. 75), teamLeadPercent for team-generated (e.g. 50).
// Unset lead source uses defaultLeadSource. Returns null when the setting is missing, blank or 0.
const pctOf = (v) => { const n = parseFloat(v); return Number.isFinite(n) && n > 0 && n <= 100 ? n : null; };
export const agentSharePercent = (t, settings, cfg = COMMISSION) => {
  const s = settings || {};
  return pctOf((leadSourceOf(t) || cfg.defaultLeadSource) === 'agent' ? s.agentLeadPercent : s.teamLeadPercent);
};
// Team leader's share (%) of an agent's deal: 100 - the agent's lead-type share when set on the
// agent's profile; otherwise the plan default (25 agent / 50 team) so older users keep their numbers.
// Order: Limited Agent custom plan (Amanda) is never overridden; then an admin per-deal override
// (deal.teamShareOverridePercent); then the agent's own split from their profile (per-agent override);
// then the team setting (COMMISSION.agent.leaderSharePercent: agent-generated 25 / team-generated 50).
export const dealOverridePercent = (t) => { const n = parseFloat(t?.teamShareOverridePercent); return Number.isFinite(n) && n >= 0 && n <= 100 ? n : null; };
export const leaderSharePercent = (t, settings, cfg = COMMISSION) => {
  if (!customPlanOf(settings) && dealOverridePercent(t) != null) return dealOverridePercent(t);
  const a = agentSharePercent(t, settings, cfg);
  return a != null ? 100 - a : cfg.agent.leaderSharePercent[leadSourceOf(t) || cfg.defaultLeadSource];
};
const settingsGetter = (s) => (typeof s === 'function' ? s : () => s);

export const capYearStart = (now = new Date(), cfg = COMMISSION) => {
  const m = (cfg.capResetMonth || 1) - 1; const d = cfg.capResetDay || 1;
  const start = new Date(now.getFullYear(), m, d);
  return start > now ? new Date(now.getFullYear() - 1, m, d) : start;
};

// Per-user plan (commissionSettings.customPlan === true, set for Limited Agents): company % = brokerSplitPercent,
// company cap = capAmount, cap year starts on capAnniversaryMonth/Day, and the lead agent's share of her deals
// counts as the lead agent's own (agent) income, not team-leader override. Blank values fall back to the plan.
export const customPlanOf = (settings) => (settings && typeof settings === 'object' && settings.customPlan === true ? settings : null);
export const planFor = (isLeader, cfg = COMMISSION, settings) => {
  const base = isLeader ? cfg.teamLeader : cfg.agent;
  const c = isLeader ? null : customPlanOf(settings);
  if (!c) return base;
  const pct = parseFloat(c.brokerSplitPercent); const cap = parseFloat(c.capAmount);
  return { ...base, brokerPercent: Number.isFinite(pct) && pct > 0 && pct <= 100 ? pct : base.brokerPercent, annualCap: Number.isFinite(cap) && cap > 0 ? cap : base.annualCap };
};
export const capYearStartFor = (now = new Date(), cfg = COMMISSION, settings) => {
  const c = customPlanOf(settings);
  const m = c && parseInt(c.capAnniversaryMonth, 10); const d = c && parseInt(c.capAnniversaryDay, 10);
  return m >= 1 && m <= 12 && d >= 1 && d <= 31 ? capYearStart(now, { ...cfg, capResetMonth: m, capResetDay: d }) : capYearStart(now, cfg);
};
export const leaderShareIsOwn = (settings) => !!customPlanOf(settings);

const zero = () => ({ gross: 0, leader: 0, broker: 0, net: 0, deals: 0, unsetLeadSource: 0 });
const byClose = (list) => [...list].sort((a, b) => closeDateOf(a) - closeDateOf(b));

// One person's commission for the cap year. closedYtd first (close-date order), then pending continues
// from the YTD cap progress. isLeader = team leader plan (no leader share).
// settings = the agent's commissionSettings (or a function deal -> settings) for lead-type splits.
export const computeCommission = (closedYtd, pending, isLeader, cfg = COMMISSION, settings) => {
  const settingsFor = settingsGetter(settings);
  const plan = planFor(isLeader, cfg, typeof settings === 'function' ? null : settings);
  const pct = plan.brokerPercent / 100;
  let paid = 0;
  const rows = { ytd: [], pending: [] };
  let bucket = rows.ytd;
  const run = (list) => byClose(list).reduce((acc, t) => {
    const gross = grossOf(t);
    const src = leadSourceOf(t);
    const leader = isLeader ? 0 : gross * (leaderSharePercent(t, settingsFor(t), cfg) / 100);
    const portion = gross - leader;
    const broker = Math.max(0, Math.min(portion * pct, plan.annualCap - paid));
    paid += broker;
    bucket.push({ deal: t, gross, leader, broker, net: portion - broker });
    return { gross: acc.gross + gross, leader: acc.leader + leader, broker: acc.broker + broker, net: acc.net + portion - broker, deals: acc.deals + 1, unsetLeadSource: acc.unsetLeadSource + (!isLeader && !src ? 1 : 0) };
  }, zero());
  const ytd = run(closedYtd);
  const capPaidYtd = paid;
  bucket = rows.pending;
  const pend = run(pending);
  return { ytd, pending: pend, ytdRows: rows.ytd, pendingRows: rows.pending, capPaidYtd, capPaidWithPending: paid, cap: plan.annualCap };
};

// Take-home for one deal list owner, used by dashboards. Splits deals into YTD closed and open.
export const takeHome = (deals, isLeader, now = new Date(), cfg = COMMISSION, settings) => {
  const start = capYearStartFor(now, cfg, typeof settings === 'function' ? null : settings);
  const closed = deals.filter((t) => isClosedDeal(t) && closeDateOf(t) >= start);
  const open = deals.filter(isOpenDeal);
  return { ...computeCommission(closed, open, isLeader, cfg, settings), closed, open };
};

// Team leader: own deals (gross) and override income (leader share of agents' deals) form one
// chronological stream by close date. eXp takes brokerPercent of each amount until the leader's
// cumulative eXp paid reaches annualCap, then 0% on everything after. Pending continues from YTD.
// agentDeals = other agents' deals (closed YTD and open), already filtered to non-leader owners.
// settingsFor = function deal -> that deal owner's commissionSettings (lead-type splits).
export const leaderCommission = (ownClosed, ownOpen, agentClosed, agentOpen, cfg = COMMISSION, settingsFor) => {
  const settingsOf = settingsGetter(settingsFor);
  const plan = cfg.teamLeader;
  const pct = plan.brokerPercent / 100;
  const share = (t) => grossOf(t) * (leaderSharePercent(t, settingsOf(t), cfg) / 100);
  let paid = 0;
  const run = (own, agents) => {
    const items = [
      ...own.map((t) => ({ kind: 'own', amount: grossOf(t), date: closeDateOf(t) })),
      // A Limited Agent's deals: the lead agent's share is her own agent income (co-agent split), not override.
      ...agents.map((t) => ({ kind: leaderShareIsOwn(settingsOf(t)) ? 'own' : 'override', amount: share(t), date: closeDateOf(t) })),
    ].sort((a, b) => a.date - b.date);
    const ownAgents = agents.filter((t) => leaderShareIsOwn(settingsOf(t))).length;
    const out = { own: { ...zero(), deals: own.length + ownAgents }, override: { ...zero(), deals: agents.length - ownAgents } };
    items.forEach((it) => {
      const counts = it.kind === 'own' || cfg.overrideCountsTowardLeaderCap;
      const broker = counts ? Math.max(0, Math.min(it.amount * pct, plan.annualCap - paid)) : it.amount * pct;
      if (counts) paid += broker;
      const o = out[it.kind];
      o.gross += it.amount; o.broker += broker; o.net += it.amount - broker;
    });
    return out;
  };
  const ytd = run(ownClosed, agentClosed);
  const capPaidYtd = paid;
  const pending = run(ownOpen, agentOpen);
  return { ytd: ytd.own, pending: pending.own, overrideYtd: ytd.override, overridePending: pending.override, capPaidYtd, capPaidWithPending: paid, cap: plan.annualCap };
};

// Shared by the dashboard card and the Pending Commission page so the numbers can't drift.
// deals = the user's deals (same list the card uses). Returns per-deal rows + total (= card value).
const DATE_KEYS = ['closingDate', 'anticipatedClosingDate', 'actualClosingDate'];
export const closingDateText = (t) => { for (const k of DATE_KEYS) if (t?.[k]) return String(t[k]); return ''; };
export const pendingCommissionBreakdown = (deals, isLeader, now = new Date(), cfg = COMMISSION, settings) => {
  const r = takeHome(deals || [], isLeader, now, cfg, settings);
  const rows = r.pendingRows.map(({ deal, gross, net }) => ({
    id: deal.id,
    ownerUid: deal.ownerUid,
    name: deal.name || deal.title || deal.transactionName || deal.propertyAddress || deal.address || 'Transaction',
    address: deal.propertyAddress || deal.address || '',
    closingDate: closingDateText(deal),
    gross,
    commission: net,
  }));
  return { rows: sortPendingRows(rows, 'date', 'asc'), total: r.pending.net };
};
// Sort by closing date (missing dates always last), commission, or name.
export const sortPendingRows = (rows, key = 'date', dir = 'asc') => {
  const m = dir === 'desc' ? -1 : 1;
  const ts = (x) => { const v = x.closingDate ? new Date(x.closingDate).getTime() : NaN; return Number.isFinite(v) ? v : null; };
  return [...rows].sort((a, b) => {
    if (key === 'date') {
      const A = ts(a); const B = ts(b);
      if (A == null && B == null) return 0;
      if (A == null) return 1;
      if (B == null) return -1;
      return (A - B) * m;
    }
    if (key === 'commission') return (a.commission - b.commission) * m;
    return String(a.name).localeCompare(String(b.name)) * m;
  });
};
